/**
 * @fileoverview Shared Evaluation UI Primitives
 * @description Small presentational pieces reused across the module's pages.
 */

import React from 'react';
import { Box, Breadcrumbs, Chip, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ChevronRight, Minus, TrendingDown, TrendingUp } from 'lucide-react';
import { BAND_COLORS } from '../constants';

const MODULE_ROOT = '/ai-evaluation-results';

/**
 * Render the score chip for a normalized result.
 * @param {Object} props - Score, band, and optional size.
 * @returns {JSX.Element} Score chip.
 */
export function ScoreChip({ score, band, size = 'small' }) {
  const tone = BAND_COLORS[band] || BAND_COLORS['not-run'];
  return (
    <Chip
      size={size}
      className="aier-score"
      label={score === null || score === undefined ? 'Not run' : score.toFixed(2)}
      sx={{ color: tone, backgroundColor: `${tone}16` }}
    />
  );
}

/**
 * Render a signed movement between two runs.
 * @param {Object} props - Score delta.
 * @returns {JSX.Element} Delta indicator.
 */
export function DeltaIndicator({ delta }) {
  if (delta === null || delta === undefined) {
    return <Typography className="aier-delta aier-delta--flat">—</Typography>;
  }
  const improved = delta > 0.005;
  const regressed = delta < -0.005;
  const Icon = improved ? TrendingUp : regressed ? TrendingDown : Minus;
  const tone = improved ? BAND_COLORS.green : regressed ? BAND_COLORS.red : '#5B6573';
  const label = delta > 0 ? `+${delta.toFixed(2)}` : delta.toFixed(2);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: tone }}>
      <Icon size={14} />
      <Typography className="aier-delta">{label}</Typography>
    </Stack>
  );
}

/**
 * Render one summary metric tile.
 * @param {Object} props - Metric label, value, caption, icon, and accent color.
 * @returns {JSX.Element} Summary metric.
 */
export function SummaryMetric({ label, value, caption, icon: Icon, color }) {
  return (
    <Paper className="aier-metric" elevation={0}>
      <Box className="aier-metric-icon" sx={{ color, backgroundColor: `${color}14` }}>
        <Icon size={20} />
      </Box>
      <Box>
        <Typography className="aier-metric-label">{label}</Typography>
        <Typography className="aier-metric-value">{value}</Typography>
        <Typography className="aier-metric-caption">{caption}</Typography>
      </Box>
    </Paper>
  );
}

/**
 * Render the navigation trail for the current depth.
 * @param {Object} props - Ordered trail entries.
 * @returns {JSX.Element} Breadcrumb trail.
 */
export function EvaluationBreadcrumbs({ trail }) {
  return (
    <Breadcrumbs
      className="aier-breadcrumbs"
      separator={<ChevronRight size={13} />}
      aria-label="Evaluation navigation"
    >
      <Link component={RouterLink} to={MODULE_ROOT} underline="hover">AI Evals</Link>
      {trail.map((entry, index) => (
        index === trail.length - 1 || !entry.to
          ? <Typography key={entry.label} className="aier-breadcrumb-current">{entry.label}</Typography>
          : <Link key={entry.label} component={RouterLink} to={entry.to} underline="hover">{entry.label}</Link>
      ))}
    </Breadcrumbs>
  );
}

/**
 * Render a centered empty state.
 * @param {Object} props - Icon, title, message, and actions.
 * @returns {JSX.Element} Empty state.
 */
export function EmptyState({ icon: Icon, title, message, children, hint }) {
  return (
    <Paper className="aier-empty-state" elevation={0}>
      <Box className="aier-empty-icon">
        <Icon size={28} />
      </Box>
      <Typography variant="h6" className="aier-empty-title">{title}</Typography>
      <Typography className="aier-empty-text">{message}</Typography>
      {children && (
        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
          {children}
        </Stack>
      )}
      {hint && <Typography className="aier-empty-hint">{hint}</Typography>}
    </Paper>
  );
}

/**
 * Render a message for a record that no longer exists.
 * @param {Object} props - Icon and message.
 * @returns {JSX.Element} Missing record notice.
 */
export function MissingRecord({ icon: Icon, message }) {
  return (
    <EmptyState icon={Icon} title="Not found" message={message}>
      <Link component={RouterLink} to={MODULE_ROOT} underline="hover">Back to projects</Link>
    </EmptyState>
  );
}
